import { z } from "zod";
import { CURRENCY_CODES } from "../money/currency.ts";

/**
 * Members, stored value and points (`03-data-model.md` § 7, `12-chain.md` § 8).
 *
 * Membership is chain-scoped, so a member read at one branch is the same
 * member at another — and `classification` is what tells the person at the
 * till which of the two they are looking at.
 */

const Money = z.string().regex(/^-?\d+$/, "minor units, as digits");
const PositiveMoney = z.string().regex(/^\d+$/, "minor units, as digits");

/**
 * `12-chain.md` § 8's three readings of one row: registered here, registered
 * elsewhere in this chain, or not one of ours at all.
 */
export const MEMBER_CLASSIFICATIONS = ["our_store", "chain_store", "non_chain"] as const;
export type MemberClassification = (typeof MEMBER_CLASSIFICATIONS)[number];

export const MemberSchema = z.object({
  id: z.uuid(),
  memberNo: z.string(),
  name: z.string().nullable(),
  phone: z.string(),
  birthday: z.string().nullable(),
  tier: z.string().nullable(),
  notes: z.string().nullable(),
  isBlocked: z.boolean(),
  pointsBalance: z.string(),
  /** Where they registered, and how this store should read them. */
  storeId: z.uuid(),
  storeName: z.string().nullable(),
  classification: z.enum(MEMBER_CLASSIFICATIONS),
  /** Absent until the member has held money — an account opens on first use. */
  balance: Money.nullable(),
  currency: z.enum(CURRENCY_CODES).nullable(),
  hasPin: z.boolean(),
  createdAt: z.string(),
});
export type Member = z.infer<typeof MemberSchema>;

export const CreateMemberSchema = z.object({
  /** The only field a till can reasonably ask a diner for. */
  phone: z.string().trim().min(3).max(32),
  name: z.string().trim().max(120).nullable().default(null),
  birthday: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
  tier: z.string().trim().max(40).nullable().default(null),
  notes: z.string().trim().max(500).nullable().default(null),
  /** Absent lets the store's own sequence allocate one. */
  memberNo: z.string().trim().max(40).nullable().default(null),
});

/**
 * Spelled out rather than derived with `.partial()`.
 *
 * `.partial()` makes a field optional but **keeps its `.default()`**, so an
 * omitted key arrives as its default rather than as absent — and a PATCH that
 * meant "rename this" would quietly reset everything it did not mention.
 */
export const UpdateMemberSchema = z.object({
  phone: z.string().trim().min(3).max(32).optional(),
  name: z.string().trim().max(120).nullable().optional(),
  birthday: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  tier: z.string().trim().max(40).nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
  memberNo: z.string().trim().max(40).nullable().optional(),
  isBlocked: z.boolean().optional(),
});

export const MemberListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  size: z.coerce.number().int().positive().max(200).default(20),
  q: z.string().trim().optional(),
  classification: z.enum(MEMBER_CLASSIFICATIONS).optional(),
});

// ── Stored value ────────────────────────────────────────────────────────────

export const TopUpSchema = z.object({
  /** What the diner handed over. The bonus is decided here, not by the caller. */
  amount: PositiveMoney,
  /** A tier by id, or absent to take whatever tier matches the amount exactly. */
  tierId: z.uuid().nullable().default(null),
  reason: z.string().trim().max(240).nullable().default(null),
});

export const AdjustBalanceSchema = z.object({
  /** Signed. A correction can go either way, which is why it needs a reason. */
  amount: Money,
  reason: z.string().trim().min(1).max(240),
});

export const SetPinSchema = z.object({
  pin: z.string().regex(/^\d{4,8}$/, "four to eight digits"),
});

export const BalanceMovementSchema = z.object({
  id: z.uuid(),
  kind: z.enum(["topup", "spend", "refund", "adjust"]),
  amount: Money,
  bonusAmount: Money,
  balanceAfter: Money,
  currency: z.enum(CURRENCY_CODES),
  earningStoreName: z.string().nullable(),
  burningStoreName: z.string().nullable(),
  orderId: z.uuid().nullable(),
  orderNo: z.string().nullable(),
  actorName: z.string().nullable(),
  reason: z.string().nullable(),
  occurredAt: z.string(),
});

export const MemberAccountSchema = z.object({
  consumerId: z.uuid(),
  currency: z.enum(CURRENCY_CODES),
  balance: Money,
  frozen: Money,
  hasPin: z.boolean(),
  /** True when the balance is shared with other stores in the chain. */
  isShared: z.boolean(),
  sharedWith: z.array(z.string()),
});

/**
 * The stored-value liability, and whose it is *(verified —
 * `storeBalancePercentage`)*.
 *
 * Money taken at one branch and spendable at another is a debt between them,
 * so the overview is per store as well as in total: `held` is what a store
 * took in and has not yet served, and it is the figure that matters when a
 * chain settles between locations.
 */
export const BalanceSummarySchema = z.object({
  currency: z.enum(CURRENCY_CODES),
  /** What the chain owes its members, in total. */
  liability: Money,
  memberCount: z.number().int(),
  byStore: z.array(
    z.object({
      storeId: z.uuid(),
      storeName: z.string(),
      toppedUp: Money,
      spent: Money,
      held: Money,
      /** Basis points of the chain's liability this store took in. */
      shareBp: z.number().int(),
    }),
  ),
});

// ── Top-up tiers ────────────────────────────────────────────────────────────

export const RechargeTierSchema = z.object({
  id: z.uuid(),
  payAmount: Money,
  bonusAmount: Money,
  isActive: z.boolean(),
  sort: z.number().int(),
});

export const SaveRechargeTierSchema = z.object({
  payAmount: PositiveMoney,
  bonusAmount: PositiveMoney.default("0"),
  isActive: z.boolean().default(true),
  sort: z.number().int().default(0),
});

// ── Points ──────────────────────────────────────────────────────────────────

export const PointsSettingsSchema = z.object({
  isEnabled: z.boolean(),
  minorUnitsPerPoint: PositiveMoney,
  minorUnitsPerPointRedeemed: PositiveMoney,
  minimumRedeemable: PositiveMoney,
});

export const SavePointsSettingsSchema = z.object({
  isEnabled: z.boolean(),
  minorUnitsPerPoint: PositiveMoney,
  minorUnitsPerPointRedeemed: PositiveMoney,
  minimumRedeemable: PositiveMoney.default("0"),
});

export const AdjustPointsSchema = z.object({
  amount: z.string().regex(/^-?\d+$/, "whole points, signed"),
  reason: z.string().trim().min(1).max(240),
});

export const PointsMovementSchema = z.object({
  id: z.uuid(),
  kind: z.enum(["earn", "redeem", "adjust", "expire"]),
  amount: z.string(),
  balanceAfter: z.string(),
  storeName: z.string().nullable(),
  orderId: z.uuid().nullable(),
  orderNo: z.string().nullable(),
  actorName: z.string().nullable(),
  reason: z.string().nullable(),
  occurredAt: z.string(),
});

/**
 * Retro-attaching a settled bill to a member *(verified — `linkableOrders`)*.
 *
 * A diner who forgot to say they were a member at the till, and asks on the
 * way out. The points are earned from the order's own net, not from a figure
 * the caller supplies.
 */
export const LinkOrderSchema = z.object({ orderId: z.uuid() });
